import { UpdateType, useImmer } from 'app/hooks';
import { merge } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { createContext } from 'use-context-selector';
import { sessionStorage } from 'app/lib/storage';
import { useSynapse } from 'app/lib/synaptik';
import { UPDATABLE_PROFILE_FIELDS } from 'app/stores/user';
import moment from 'moment';
import { Profile } from 'app/types/Profile';

export interface OnboardingDataType {
  dob?: string;
  gender?: string;
  firstName?: string;
  lastName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  currentState?: string;
}

export interface OnboardingContextType {
  cache: OnboardingDataType;
  profile: OnboardingDataType;
  errors: {
    profile?: string;
    dob?: string;
    gender?: string;
    currentState?: string;
  };
  setState?: UpdateType<OnboardingContextType>;
  clearOnboarding?: () => void;
}

function getOnboardingDataFromProfile(profile: Profile | null): Partial<OnboardingDataType> {
  if (!profile) {
    return {};
  }

  const profileData =
    Object.fromEntries &&
    Object.fromEntries(
      UPDATABLE_PROFILE_FIELDS.map((key) => [key, profile[key]]).filter(
        ([_key, value]) => value !== null && value !== undefined
      )
    );

  if (profileData.dob) {
    return { ...profileData, dob: moment(profileData.dob).format('MM/DD/YYYY') };
  }

  return profileData;
}

const DEFAULT_VALUES = { cache: {}, profile: {}, errors: {} };

export const OnboardingContext = createContext<OnboardingContextType>({ cache: {}, profile: {}, errors: {} });

export const OnboardingProvider: React.FunctionComponent = ({ children }) => {
  // @ts-ignore TODO: fix user.state.profile typing so that this comment can be removed
  const profile = useSynapse(({ user }) => getOnboardingDataFromProfile(user.state.profile));

  const [state, setState] = useImmer<OnboardingContextType>(
    (merge(
      {},
      DEFAULT_VALUES,
      {
        profile,
        cache: profile,
      },
      sessionStorage.get('onboarding')
    ) as unknown) as OnboardingContextType
  );

  const clearOnboarding = useCallback(() => {
    sessionStorage.remove('onboarding');
    setState(DEFAULT_VALUES);
  }, [setState]);

  const value = useMemo(
    () => ({
      ...state,
      setState,
      clearOnboarding,
    }),
    [state, setState, clearOnboarding]
  );

  useEffect(() => {
    sessionStorage.set('onboarding', state);
  }, [state]);

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};
